export const deleteEmptyFieldsFromObject = <T>(obj: T): T => {
  const objCopy = { ...obj };

  for (const key in objCopy) {
    if (Object.prototype.hasOwnProperty.call(objCopy, key)) {
      const value = objCopy[key];

      if (value == null || value === -1 || (typeof value === "string" && value.trim() === "")) {
        delete objCopy[key];
      } else if (Array.isArray(value)) {
        const cleanedArray = value
          .map((item) => (typeof item === "object" && item !== null ? deleteEmptyFieldsFromObject(item) : item))
          .filter((item) => {
            if (typeof item === "object" && item !== null) {
              return Object.keys(item).length > 0;
            }
            return item != null && item !== -1 && (typeof item !== "string" || item.trim() !== "");
          });

        if (cleanedArray.length > 0) {
          (objCopy as any)[key] = cleanedArray;
        } else {
          delete objCopy[key];
        }
      } else if (typeof value === "object" && value !== null) {
        const cleanedObject = deleteEmptyFieldsFromObject(value);

        if (Object.keys(cleanedObject).length > 0) {
          (objCopy as any)[key] = cleanedObject;
        } else {
          delete objCopy[key];
        }
      }
    }
  }

  return objCopy;
};

export const compareObjects = <T>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) {
    return true;
  }

  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1) as Array<keyof T>;
  const keys2 = Object.keys(obj2) as Array<keyof T>;

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }

    const val1 = obj1[key];
    const val2 = obj2[key];

    const areObjects = typeof val1 === "object" && val1 !== null && typeof val2 === "object" && val2 !== null;
    if ((areObjects && !compareObjects(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
};


/**
 * @param obj unknown object
 * @returns the same object with the same structure but all values set to null.
 */
export const setAllValuesToNull = <T>(obj: T): T => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => setAllValuesToNull(item)) as unknown as T;
  }

  const newObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = (obj as any)[key];
      if (typeof value === "object" && value !== null) {
        newObj[key] = setAllValuesToNull(value);
      } else {
        newObj[key] = null;
      }
    }
  }

  return newObj;
};

export const getValueByPath = <T>(obj: Record<string, any>, path: string): T | undefined => {
  const keys = path.split(".");
  let current: any = obj;
  let prevKey: string | undefined;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (Array.isArray(current)) {
      const numericKey = Number(key);

      // Support custom_data.N.field_value with sparse arrays by resolving N via field_index.
      if (prevKey === "custom_data" && Number.isInteger(numericKey)) {
        current = current.find((item) => Number(item?.field_index) === numericKey) ?? current[numericKey];
      } else if (Number.isInteger(numericKey)) {
        current = current[numericKey];
      } else {
        return undefined;
      }
    } else {
      current = current[key];
    }

    prevKey = key;
  }

  return current as T;
};

export const getCustomDataValue = <T>(
  obj: Record<string, any>,
  fieldIndex: number,
  prefix?: string,
): T | undefined => {
  const customDataPath = prefix ? `${prefix}.custom_data` : "custom_data";
  const customData = getValueByPath<Array<{ field_index?: number | null; field_value?: T }>>(obj, customDataPath);

  if (!customData?.length) {
    return undefined;
  }

  return customData.find((row) => Number(row.field_index) === fieldIndex)?.field_value;
};
